var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useCallback, useRef, useEffect } from 'react';
var SEGMENT_CONFIG = {
    month: {
        maxLength: 2,
        maxValue: 12,
        autoAdvanceThreshold: 2,
        paddingLength: 2,
    },
    day: {
        maxLength: 2,
        maxValue: 31,
        paddingLength: 2,
    },
    year: {
        maxLength: 4,
        paddingLength: 4,
    },
};
var validateSegmentValue = function (segment, value) {
    var config = SEGMENT_CONFIG[segment];
    var numValue = parseInt(value, 10);
    if (segment === 'year' && value.length > 0) {
        var firstChar = value.charAt(0);
        if (firstChar !== '1' && firstChar !== '2') {
            return { isValid: false, shouldReplace: true };
        }
    }
    if (config.maxValue && numValue > config.maxValue) {
        return { isValid: false, shouldReplace: true };
    }
    return { isValid: true, shouldReplace: false };
};
var shouldAutoAdvance = function (segment, value) {
    var config = SEGMENT_CONFIG[segment];
    if (!value)
        return false;
    if (segment === 'month') {
        if (value.length === 1 &&
            parseInt(value, 10) >= (config.autoAdvanceThreshold || 0)) {
            return true;
        }
        return value.length === 2 && parseInt(value, 10) <= 12;
    }
    if (segment === 'day') {
        return value.length === 2 && parseInt(value, 10) <= 31;
    }
    return false;
};
var padSegmentValue = function (segment, value) {
    var config = SEGMENT_CONFIG[segment];
    return value.padStart(config.paddingLength, '0');
};
var processSegmentInput = function (segment, currentValue, newInput, justFocused) {
    var config = SEGMENT_CONFIG[segment];
    var cleanInput = newInput.replace(/\D/g, '');
    var finalValue = justFocused
        ? cleanInput.slice(-1)
        : cleanInput.slice(0, config.maxLength);
    var shouldAdvance = false;
    var validation = validateSegmentValue(segment, finalValue);
    if (validation.shouldReplace) {
        finalValue = cleanInput.slice(-1);
        shouldAdvance = true;
    }
    else if (shouldAutoAdvance(segment, finalValue)) {
        shouldAdvance = true;
    }
    var displayValue = padSegmentValue(segment, finalValue);
    return { finalValue: finalValue, displayValue: displayValue, shouldAdvance: shouldAdvance };
};
var getNextSegmentRef = function (segment, refs) {
    var dayRef = refs.dayRef, yearRef = refs.yearRef;
    switch (segment) {
        case 'month':
            return dayRef;
        case 'day':
            return yearRef;
        default:
            return null;
    }
};
var getPreviousSegmentRef = function (segment, refs) {
    var monthRef = refs.monthRef, dayRef = refs.dayRef;
    switch (segment) {
        case 'day':
            return monthRef;
        case 'year':
            return dayRef;
        default:
            return null;
    }
};
var focusAndSelectSegment = function (ref) {
    if (ref === null || ref === void 0 ? void 0 : ref.current) {
        ref.current.focus();
        setTimeout(function () {
            if (ref.current) {
                ref.current.select();
            }
        }, 0);
    }
};
var parseDate = function (dateString) {
    var parts = dateString.split('/');
    return {
        month: parts[0] || '',
        day: parts[1] || '',
        year: parts[2] || '',
    };
};
var formatDateString = function (month, day, year) {
    if (!month && !day && !year)
        return '';
    return month + "/" + day + "/" + year;
};
export var useSegmentedDateInput = function (_a) {
    var _b = _a.value, value = _b === void 0 ? '' : _b, onChange = _a.onChange;
    var monthRef = useRef(null);
    var dayRef = useRef(null);
    var yearRef = useRef(null);
    var _c = useState(function () { return parseDate(value); }), segments = _c[0], setSegments = _c[1];
    var _d = useState({ month: '', day: '', year: '' }), rawInput = _d[0], setRawInput = _d[1];
    var _e = useState(null), justFocused = _e[0], setJustFocused = _e[1];
    useEffect(function () {
        var parsed = parseDate(value);
        setSegments(parsed);
        setRawInput({
            month: parsed.month.replace(/^0/, '') || '',
            day: parsed.day.replace(/^0/, '') || '',
            year: parsed.year || '',
        });
    }, [value]);
    var handleSegmentChange = useCallback(function (segment, newValue) {
        var _a, _b;
        var _c;
        var isJustFocused = justFocused === segment;
        var currentRawValue = rawInput[segment] || '';
        var _d = processSegmentInput(segment, currentRawValue, newValue, isJustFocused), finalValue = _d.finalValue, displayValue = _d.displayValue, shouldAdvance = _d.shouldAdvance;
        if (isJustFocused) {
            setJustFocused(null);
        }
        var newRawInput = __assign(__assign({}, rawInput), (_a = {}, _a[segment] = finalValue, _a));
        setRawInput(newRawInput);
        var newSegments = __assign(__assign({}, segments), (_b = {}, _b[segment] = displayValue, _b));
        setSegments(newSegments);
        if (shouldAdvance) {
            var refs = { monthRef: monthRef, dayRef: dayRef, yearRef: yearRef };
            var nextRef = getNextSegmentRef(segment, refs);
            (_c = nextRef === null || nextRef === void 0 ? void 0 : nextRef.current) === null || _c === void 0 ? void 0 : _c.focus();
        }
        if (onChange) {
            var formattedDate = formatDateString(newSegments.month, newSegments.day, newSegments.year);
            onChange(formattedDate);
        }
    }, [segments, rawInput, onChange, justFocused]);
    var handleSegmentClick = useCallback(function (segment) {
        setJustFocused(segment);
        var refMap = {
            month: monthRef,
            day: dayRef,
            year: yearRef,
        };
        var ref = refMap[segment];
        focusAndSelectSegment(ref);
    }, []);
    var handleSegmentFocus = useCallback(function (segment) {
        setJustFocused(segment);
    }, []);
    var handleKeyDown = useCallback(function (segment, event) {
        var _a, _b;
        var _c;
        var refs = { monthRef: monthRef, dayRef: dayRef, yearRef: yearRef };
        if (event.key === 'Backspace' ||
            event.key === '/' ||
            event.key === 'ArrowLeft' ||
            event.key === 'ArrowRight' ||
            event.key === 'Tab') {
            setJustFocused(null);
        }
        if (event.key === 'Backspace') {
            event.preventDefault();
            var newRawInput = __assign(__assign({}, rawInput), (_a = {}, _a[segment] = '', _a));
            setRawInput(newRawInput);
            var newSegments = __assign(__assign({}, segments), (_b = {}, _b[segment] = '', _b));
            setSegments(newSegments);
            if (onChange) {
                var formattedDate = formatDateString(newSegments.month, newSegments.day, newSegments.year);
                onChange(formattedDate);
            }
            return;
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            var prevRef = getPreviousSegmentRef(segment, refs);
            focusAndSelectSegment(prevRef);
            return;
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            var nextRef = getNextSegmentRef(segment, refs);
            focusAndSelectSegment(nextRef);
            return;
        }
        if (event.key === '/') {
            event.preventDefault();
            var nextRef = getNextSegmentRef(segment, refs);
            (_c = nextRef === null || nextRef === void 0 ? void 0 : nextRef.current) === null || _c === void 0 ? void 0 : _c.focus();
        }
    }, [segments, rawInput, onChange]);
    var handleSegmentKeyDown = useCallback(function (segment, event) {
        if (event.key >= '0' && event.key <= '9') {
            var target = event.target;
            var start = target.selectionStart;
            var end = target.selectionEnd;
            if (justFocused === segment &&
                start === 0 &&
                end === target.value.length &&
                target.value.length > 0) {
                event.preventDefault();
                handleSegmentChange(segment, event.key);
            }
            else if (start === end && start === target.value.length) {
                event.preventDefault();
                var currentRawValue = rawInput[segment] || '';
                var newValue = currentRawValue + event.key;
                handleSegmentChange(segment, newValue);
            }
        }
        if (event.key < '0' || event.key > '9') {
            handleKeyDown(segment, event);
        }
    }, [handleSegmentChange, handleKeyDown, justFocused, rawInput]);
    return {
        monthRef: monthRef,
        dayRef: dayRef,
        yearRef: yearRef,
        segments: segments,
        handleSegmentChange: handleSegmentChange,
        handleSegmentKeyDown: handleSegmentKeyDown,
        handleSegmentClick: handleSegmentClick,
        handleSegmentFocus: handleSegmentFocus,
    };
};
export default useSegmentedDateInput;
